import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { X, FileText, Check, Copy } from '@phosphor-icons/react';
import useCopyToClipboard from '../../../../hooks/useCopyToClipboard';
import { useAlert } from '../../../../hooks/useAlert';
import MessagePopup from '../../../common/MessagePopup';
import { useDashboard } from '../../../../hooks/useDashboard';
import { enhanceSystemPromptApi } from '../../../../utils/apis';
import { useMutation } from '@tanstack/react-query';
import { useTemplates } from '../../../../hooks/useTemplates';
import PromptConfiguration from './PromptConfiguration';
import CodeBlockRenderer from './CodeBlockRenderer';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../utils/redux';
import {
  setTemplatePrompt,
  setActiveTab,
  startProcessing,
  processSuccess,
  processError,
  clearMessages,
  setScenarios,
  clearResult,
  type Scenario,
} from '../../../../utils/redux/slices/enhanceSystemPromptSlice';

interface EnhanceSystemPromptDrawerProps {
  open: boolean;
  onClose: () => void;
}

const EnhanceSystemPromptDrawer: React.FC<EnhanceSystemPromptDrawerProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const {
    templatePrompt,
    scenarios,
    activeTab,
    rawEnhancedPrompt,
    isSubmitting,
    isProcessing,
    hasResult,
    error,
    successMessage,
  } = useSelector((state: RootState) => state.enhanceSystemPrompt);

  const { alert, setAlert } = useAlert();
  const { copied, copy } = useCopyToClipboard();
  const { selectedAssistant } = useDashboard();
  const { data: templates } = useTemplates();

  // Track current agent to detect changes
  const currentAgentId = useRef<string | null>(null);

  // Clear enhance system prompt state when switching agents
  useEffect(() => {
    if (selectedAssistant?._id !== currentAgentId.current) {
      // Agent has changed, clear the enhance system prompt state
      if (currentAgentId.current !== null) {
        dispatch(clearResult());
      }
      currentAgentId.current = selectedAssistant?._id || null;
    }
  }, [selectedAssistant, dispatch]);

  // Convert selected assistant file to string and populate template prompt
  useEffect(() => {
    if (selectedAssistant?.file && open && !templatePrompt) {
      const fileString = JSON.stringify(selectedAssistant.file, null, 2);
      dispatch(setTemplatePrompt(fileString));
    }
  }, [selectedAssistant, open, templatePrompt, dispatch]);

  // Handle success/error messages
  useEffect(() => {
    if (successMessage) {
      setAlert({
        message: successMessage,
        severity: 'success',
        open: true,
      });
      dispatch(clearMessages());
    }
  }, [successMessage, setAlert, dispatch]);

  useEffect(() => {
    if (error) {
      setAlert({
        message: error,
        severity: 'error',
        open: true,
      });
      dispatch(clearMessages());
    }
  }, [error, setAlert, dispatch]);

  const { mutateAsync: enhanceSystemPrompt } = useMutation({
    mutationFn: enhanceSystemPromptApi,
    onSuccess: data => {
      const rawPrompt = data?.data?.enhanced_prompt || '';

      if (rawPrompt) {
        dispatch(processSuccess(rawPrompt));
      } else {
        dispatch(
          processError(
            'Prompt optimization completed, but could not extract valid data. Check console for raw response.'
          )
        );
      }
    },
    onError: (error: any) => {
      dispatch(processError(`Processing failed: ${error.message}`));
    },
  });

  const handleSubmit = async () => {
    if (!templatePrompt.trim()) {
      dispatch(processError('Please enter a template prompt'));
      return;
    }

    if (scenarios.length === 0) {
      dispatch(processError('Please add at least one scenario'));
      return;
    }

    // Find the matching template and determine type
    let templateType = '';
    if (selectedAssistant?.templateId && templates?.data?.templates) {
      const matchingTemplate = templates.data.templates.find(
        (template: any) => template._id === selectedAssistant.templateId
      );

      if (matchingTemplate) {
        switch (matchingTemplate.name) {
          case 'Automate Reservation':
            templateType = 'reservation';
            break;
          case 'General Queries':
            templateType = 'General Support';
            break;
          case 'Food Ordering':
            templateType = 'Food Ordering';
            break;
          default:
            templateType = '';
        }
      }
    }

    // Prepare the data for processing
    const formattedScenarios = scenarios.map((scenario: any) => {
      const cleanScenario = { ...scenario };
      if (!cleanScenario.description?.trim()) {
        delete cleanScenario.description;
      }
      return cleanScenario;
    });

    const updatedTemplatePrompt = templatePrompt.replace(/\\"/g, '"');

    const payload = {
      template_prompt: updatedTemplatePrompt,
      scenarios: formattedScenarios,
      agent_type: templateType,
    };

    dispatch(startProcessing(selectedAssistant?._id || ''));
    await enhanceSystemPrompt(payload);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    dispatch(setActiveTab(newValue));
  };

  const renderResultTab = () => (
    <Box
      sx={{
        overflow: 'auto',
        flexGrow: 1,
        px: 3,
        pb: 3,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      {rawEnhancedPrompt ? (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: 'custom.textBlue',
              }}
            >
              Enhanced System Prompt
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={copied ? <Check size={16} /> : <Copy size={16} />}
              onClick={() => copy(rawEnhancedPrompt)}
              sx={{
                fontSize: 12,
                fontWeight: 500,
                borderRadius: '6px',
                textTransform: 'none',
                borderColor: copied ? '#4CAF50' : '#DCE0E7',
                color: copied ? '#4CAF50' : '#6B6F80',
                '&:hover': {
                  borderColor: copied ? '#4CAF50' : '#A8B2C7',
                  backgroundColor: copied ? '#f1f8e9' : '#f8f9fa',
                },
              }}
            >
              {copied ? 'Copied!' : 'Copy All'}
            </Button>
          </Box>

          <CodeBlockRenderer content={rawEnhancedPrompt} isMarkdown={true} />
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: 'lightGray.text',
          }}
        >
          <FileText size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
          <Typography variant="body2">
            {isProcessing
              ? 'Processing your prompt enhancement... This may take 10-15 minutes.'
              : 'No result available yet. Process a prompt enhancement to see the result here.'}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              width: '600px',
              p: 0,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        {/* Sticky Header Section */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: '#FFFFFF',
            px: 3,
            pt: 3,
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            {/* Close Button and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  '&:hover': { backgroundColor: '#E8EBF1' },
                  transition: 'all 0.2s ease',
                }}
              >
                <X size={18} />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 18,
                  color: 'custom.textBlue',
                  letterSpacing: '-0.2px',
                }}
              >
                Enhance System Prompt
                {isProcessing && (
                  <Typography
                    component="span"
                    sx={{ ml: 1, fontSize: 14, color: '#666' }}
                  >
                    (Processing...)
                  </Typography>
                )}
              </Typography>
            </Box>

            {/* Submit Button */}
            {activeTab === 0 && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  borderRadius: '8px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {isSubmitting ? 'Processing...' : 'Enhance Prompt'}
              </Button>
            )}
          </Box>

          <Divider
            sx={{
              borderColor: 'rgba(0, 0, 0, 0.08)',
              mb: 1,
              mx: -3,
            }}
          />

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, mx: -3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="enhance system prompt tabs"
              sx={{
                minHeight: 'unset',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 'unset',
                  py: 1,
                  px: 2,
                  mx: 0.5,
                },
              }}
            >
              <Tab label="Configuration" value={0} />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Result
                    {hasResult && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: '#4CAF50',
                        }}
                      />
                    )}
                  </Box>
                }
                value={1}
              />
            </Tabs>
          </Box>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 ? (
          <PromptConfiguration
            setScenarios={(scenarios: Scenario[]) =>
              dispatch(setScenarios(scenarios))
            }
            scenarios={scenarios}
            templatePrompt={templatePrompt}
            setTemplatePrompt={(prompt: string) =>
              dispatch(setTemplatePrompt(prompt))
            }
          />
        ) : (
          renderResultTab()
        )}
      </Drawer>

      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default EnhanceSystemPromptDrawer;
